import { gql } from '@apollo/client';
import { Box } from '@chakra-ui/react';
import { format } from 'date-fns';
import { t } from 'i18next';
import { ReactNode, useMemo } from 'react';
import { CellProps, Column, usePagination, useTable } from 'react-table';
import { generateTableCellComponent } from '~components/ui/TableCell';
import { AdminProfilesTable_UserFragment } from './__generated__/useAdminProfilesTable.graphql';
import { AdminProfilesActionsCell } from './AdminProfilesActionsCell';
import { ServiceAdminRolesCell } from './ServiceAdminRolesCell';
import { Columns } from './types';

const TableCell = generateTableCellComponent<AdminProfilesTable_UserFragment, ReactNode>();

const TableCellBold = generateTableCellComponent<AdminProfilesTable_UserFragment, ReactNode>({
  isBold: true,
});

const DateCell = ({ value }: { value?: string | null }) => (
  <Box whiteSpace="nowrap">{value ? format(new Date(value), 'MMM d, y') : '-'}</Box>
);

interface Props {
  data: AdminProfilesTable_UserFragment[];
  pageIndex: number;
  pageSize: number;
  totalRowCount: number;
  onEdit: (user: AdminProfilesTable_UserFragment) => void;
  onRevoked: () => void;
}

export function useAdminProfilesTable({
  data,
  pageIndex,
  pageSize,
  totalRowCount,
  onEdit,
  onRevoked,
}: Props) {
  const columns = useMemo<Array<Column<AdminProfilesTable_UserFragment>>>(
    () => [
      {
        id: Columns.Name,
        Header: t('name'),
        accessor: (user) => `${user.givenName} ${user.familyName}`,
        Cell: TableCellBold,
      },
      {
        id: Columns.Access,
        Header: t('access'),
        accessor: (user) => user.serviceAdminRoles,
        Cell: ServiceAdminRolesCell,
      },
      {
        id: Columns.Email,
        Header: t('email'),
        accessor: (user) => user.email,
        Cell: TableCell,
      },
      {
        id: Columns.DateCreated,
        Header: t('dateCreated'),
        accessor: (user) => user.createdAt,
        Cell: DateCell,
      },
      {
        id: Columns.LastLogin,
        Header: t('lastLogin'),
        accessor: (user) => user.lastLogin,
        Cell: DateCell,
      },
      {
        id: Columns.Actions,
        Header: '',
        accessor: () => null,
        Cell: (props: CellProps<AdminProfilesTable_UserFragment>) => (
          <AdminProfilesActionsCell {...props} onEdit={onEdit} onRevoked={onRevoked} />
        ),
        width: '50px',
        maxWidth: 50,
        minWidth: 50,
      },
    ],
    [onEdit, onRevoked],
  );

  return useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex,
        pageSize,
      },
      autoResetPage: false,
      manualPagination: true,
      pageCount: Math.ceil(totalRowCount / pageSize),
    },
    usePagination,
  );
}

gql`
  fragment AdminProfilesTable_user on User {
    ...ConfigureAdminRolesModal_user
    createdAt
    lastLogin
  }
`;
